import React, { PropsWithChildren, createContext, useContext, useState } from 'react';

export interface InlineAccordionProps {
  /**
   * Additional CSS class names
   */
  className?: string;
  /**
   * Size variant - affects header height
   * sm = 2rem (32px), md = 2.5rem (40px), lg = 3rem (48px)
   */
  size?: 'sm' | 'md' | 'lg';
  /**
   * Style variant
   * minimal = no border (default)
   * bordered = border around entire accordion with rounded corners
   */
  variant?: 'minimal' | 'bordered';
  /**
   * Allow multiple items to be expanded simultaneously
   * Default: false (only one item can be open at a time)
   * Set to true in documentation to allow multiple items open
   */
  allowMultiple?: boolean;
  /**
   * Controlled mode - external state management
   */
  controlled?: boolean;
  /**
   * Array of expanded item IDs (controlled mode)
   */
  expandedItems?: string[];
  /**
   * Callback when an item is expanded/collapsed
   */
  onExpandChange?: (id: string, isExpanded: boolean) => void;
  /**
   * Additional styles
   */
  style?: React.CSSProperties;
}

interface AccordionContextValue {
  expandedItems: Set<string>;
  allowMultiple: boolean;
  controlled: boolean;
  size: 'sm' | 'md' | 'lg';
  onItemToggle: (id: string) => void;
}

export const InlineAccordionContext = createContext<AccordionContextValue>({
  expandedItems: new Set(),
  allowMultiple: true,
  controlled: false,
  size: 'md',
  onItemToggle: () => {},
});

export const InlineAccordion: React.FC<PropsWithChildren<InlineAccordionProps>> = ({
  children,
  className = '',
  size = 'md',
  variant = 'minimal',
  allowMultiple = false,
  controlled = false,
  expandedItems = [],
  onExpandChange,
  style,
}) => {
  // Internal state for uncontrolled mode
  const [internalExpanded, setInternalExpanded] = useState<Set<string>>(new Set());
  
  // Use external or internal state based on controlled prop
  const expanded = controlled 
    ? new Set(expandedItems) 
    : internalExpanded;
  
  const handleItemToggle = (id: string) => {
    const isCurrentlyExpanded = expanded.has(id);
    
    if (controlled) {
      // In controlled mode, just notify parent
      onExpandChange?.(id, !isCurrentlyExpanded);
    } else {
      // In uncontrolled mode, manage state internally
      setInternalExpanded(prev => {
        const next = new Set(prev);
        
        if (isCurrentlyExpanded) {
          next.delete(id);
        } else {
          // If not allowing multiple, clear all first
          if (!allowMultiple) {
            next.clear();
          }
          next.add(id);
        }
        
        return next;
      });
      
      // Still notify parent in uncontrolled mode if callback provided
      onExpandChange?.(id, !isCurrentlyExpanded);
    }
  };
  
  const contextValue: AccordionContextValue = {
    expandedItems: expanded,
    allowMultiple,
    controlled,
    size,
    onItemToggle: handleItemToggle,
  };
  
  const accordionClasses = [
    'ui-inline-accordion',
    `ui-inline-accordion--${size}`,
    variant && `ui-inline-accordion--${variant}`,
    className
  ].filter(Boolean).join(' ');
  
  return (
    <InlineAccordionContext.Provider value={contextValue}>
      <div className={accordionClasses} style={style}>
        {children}
      </div>
    </InlineAccordionContext.Provider>
  );
};

export const useInlineAccordion = () => {
  const context = useContext(InlineAccordionContext);
  if (!context) {
    throw new Error('useInlineAccordion must be used within InlineAccordion');
  }
  return context;
};
